<script lang="typescript">
  import {getTealComponentVersion} from './getTealComponentVersion';
  import {fetchPageConfig} from './fetchPageConfig';
  import {failSafeQuerySelector} from './failSafeQuerySelector';
  import {isTealElement} from './isTealElement';
  import type {TargetPageConfigSchema} from '@tts-intern/teal-output-schema/schema/__gen/target-page-config-schema';
  import {
    concatMap,
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap
  } from "rxjs/operators";
  import {fromEvent, Subject,ReplaySubject, merge, combineLatest, from} from "rxjs";
  import {nanoid} from "nanoid";
  import {onMount} from "svelte";
  import {get_current_component} from "svelte/internal";
  import {initializeHotspotIntersectionWatcher,checkIntersection} from "./hotspotIntersectionWatcher";

  export let ns: string | undefined = undefined;
  export let pageid: string | undefined = undefined;
  export let helpid: string | undefined = undefined;
  $: css = (ns && pageid) ? `https://content.dobbi.app/ns/${ns}/page/${pageid}/style.css` : "";

  const component = get_current_component();
  const tealComponentVersion = getTealComponentVersion();

  const onDestroySubject = new Subject();
  onMount(() => () => onDestroySubject.next())

  let show: boolean = JSON.parse(localStorage.getItem("teal-viewer-show") ?? true.toString());
  const showSubject = new ReplaySubject<boolean>(1);
  $: showSubject.next(show);

  const showLocalStorageObservable = showSubject.pipe(tap(show => localStorage.setItem("teal-viewer-show", JSON.stringify(show))));
  const mayShowProperty = () => ({...(show ? {show: ""} : {})});

  let iframeVideoSrc = "";

  let activeElement: HTMLElement | null = null;
  const activeElementSubject = new Subject<HTMLElement | null>();
  $: activeElementSubject.next(activeElement);

  initializeHotspotIntersectionWatcher( component, ( elem ) => {
    // track hotspot intersection
    component?.dispatchEvent(new CustomEvent("teal-viewer-hotspot-overlapped", {
      bubbles: true,
      composed: true,
      detail: elem
    }));
  });

  const focusObservable = fromEvent(window, 'focusin').pipe(
    map(() => document.activeElement),
    filter((element): element is HTMLElement => element instanceof HTMLElement),
    filter((element) => !isTealElement(element)),
    tap((element) => checkIntersection( element )),
    tap((element) => activeElement = element)
  );

  interface HelpPage {
    helpId: string;
    type: 'DEFAULT' | 'INITIAL' | 'FIELD';
    internalId: string;
  }

  interface FieldHelpPage extends HelpPage {
    type: 'FIELD';
    htmlElement: HTMLElement | null;
  }

  const pageConfigSubject = new Subject<Promise<TargetPageConfigSchema | null>>();
  $: pageConfigSubject.next(fetchPageConfig({ns, pageid}));

  const pageConfigObservable = pageConfigSubject.asObservable().pipe(
    switchMap(e => e),
    filter((e): e is TargetPageConfigSchema => e !== null)
  );

  const defaultHelpPageObservable = pageConfigObservable.pipe(
    map(pageConfig => pageConfig.default?.id ? {
      internalId: nanoid(12),
      type: 'DEFAULT',
      helpId: pageConfig.default.id
    } : null),
    shareReplay(1)
  );

  const initialHelpPageObservable = pageConfigObservable.pipe(
    map(pageConfig => pageConfig.initial?.id ? {
      internalId: nanoid(12),
      type: 'INITIAL' as 'INITIAL',
      helpId: pageConfig.initial.id
    } : null),
    shareReplay(1)
  );

  const fieldHelpPagesObservable = pageConfigObservable.pipe(
    map((pageConfig): FieldHelpPage[] => pageConfig?.fields.map(({id, selector}) => ({
      internalId: nanoid(12),
      type: 'FIELD' as 'FIELD',
      helpId: id,
      htmlElement: failSafeQuerySelector(selector)
    }))),
    shareReplay(1)
  );

  let activeHelpPage: HelpPage | null = null;
  const activeHelpPageSubject = new Subject<HelpPage | null>();
  $: activeHelpPageSubject.next(activeHelpPage);

  const useInitialHelpPageObservable = combineLatest([initialHelpPageObservable, activeElementSubject]).pipe(
    filter(([initialHelp, element]) => element === null && initialHelp !== null),
    tap(([initialHelp]) => activeHelpPage = initialHelp)
  );

  const useHelpPageWithActiveElementObservable = combineLatest([fieldHelpPagesObservable, defaultHelpPageObservable, activeElementSubject]).pipe(
    filter(([_, __, element]) => element !== null),
    tap(([pages, defaultHelp, element]) => activeHelpPage = pages.find(({htmlElement}) => element === htmlElement) ?? defaultHelp)
  );

  const previousPageSubject = new Subject<undefined>();
  const previous = () => previousPageSubject.next(undefined);
  const previousPageObservable = combineLatest([activeHelpPageSubject, fieldHelpPagesObservable, initialHelpPageObservable, previousPageSubject]).pipe(
    tap(([activeHelpPage, pages]) => {
      const pagesWithHtmlElements = pages.filter(page => page.htmlElement);
      const previousIdx = pagesWithHtmlElements.findIndex((page) => page.helpId === activeHelpPage.helpId) - 1;
      activeElement = previousIdx < 0 ? null : pagesWithHtmlElements[previousIdx].htmlElement;
    })
  );

  const nextPageSubject = new Subject<undefined>();
  const next = () => nextPageSubject.next(undefined);
  const nextPageObservable = combineLatest([activeHelpPageSubject, fieldHelpPagesObservable, nextPageSubject]).pipe(
    tap(([activeHelpPage, pages]) => {
      const pagesWithHtmlElements = pages.filter(page => page.htmlElement);
      const nextIdx = pagesWithHtmlElements.findIndex((page) => page.helpId === activeHelpPage.helpId) + 1;
      if (nextIdx < pagesWithHtmlElements.length) {
        activeElement = pagesWithHtmlElements[nextIdx].htmlElement;
      }
    }));

  //
  // top level components which are appendend to the body element
  //
  const createTopLevelDiv = () => {
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.top = '0';
    div.style.left= '0';
    div.style.zIndex = (2147483647-1).toString();
    div.style.isolation = 'isolation';
    div.style.visibility = 'var(--teal-viewer-visibility, hidden)';
    return div;
  };
  const topLevelDiv = createTopLevelDiv();

  const createLogo = () => {
    const element = document.createElement('teal-logo');
    element.style.height = "100%";
    element.style.width = "100%";
    return element;
  };

  const createOpenButton = ({logo}: { logo: HTMLElement }) => {
    const element = document.createElement('teal-open-button');
    element.appendChild(logo);
    element.style.position = "fixed";
    element.style.right = "0";
    element.style.top = "50%";
    return {
      element,
      readyObservable: fromEvent(element, 'teal-open-button-ready').pipe(shareReplay(1))
    };
  };

  const openButton = createOpenButton({
    logo: createLogo()
  });
  const openButtonObservable = combineLatest([openButton.readyObservable, showSubject]).pipe(
    map(([event, viewerIsShown]) => ({button: event.target, viewerIsShown})),
    tap(({button, viewerIsShown}) => button.show = !viewerIsShown)
  );
  topLevelDiv.appendChild(openButton.element);

  const createHoverButton = ({logo, htmlElement}: { logo: HTMLElement, htmlElement: HTMLElement }) => {
    const element = document.createElement('teal-hover-button') as HTMLElement & { element: HTMLElement };
    element.appendChild(logo);
    element.style.zIndex = '100';
    const readyObservable = fromEvent(element, 'teal-hover-button-ready').pipe(shareReplay(1));
    readyObservable.pipe(tap(() => element.element = htmlElement), take(1)).subscribe();

    return {
      element,
      readyObservable
    };
  };

  const IGNORE_MOUSE_ENTER_FROM = ["INPUT", "SELECT", "TEXTAREA"];
  const hoverButtonObservable = fieldHelpPagesObservable.pipe(
    concatMap(e => from(e)),
    filter(({htmlElement}) => htmlElement && !IGNORE_MOUSE_ENTER_FROM.includes(htmlElement.tagName)),
    map(({htmlElement}) => createHoverButton({htmlElement, logo: createLogo()})),
    tap(({element, readyObservable}) => {
      combineLatest([readyObservable, showSubject]).pipe(
        map(([event, viewerIsShown]) => ({button: event.target, viewerIsShown})),
        tap(({button, viewerIsShown}) => button.show = viewerIsShown)
      ).subscribe();
      topLevelDiv.appendChild(element);
    })
  );

  const createHighlight = () => {
    const element = document.createElement('teal-highlight') as HTMLElement & { element: HTMLElement };
    element.style.zIndex = '10';
    return {
      element,
      readyObservable: fromEvent(element, 'teal-highlight-ready').pipe(shareReplay(1))
    };
  };
  const highlight = createHighlight();

  const highlightObservable = combineLatest([highlight.readyObservable, showSubject, activeHelpPageSubject.pipe(map(help => help !== null && help.type === 'FIELD')), activeElementSubject]).pipe(
    map(([event, viewerIsShown, fieldHelpExists, activeElement]) => ({
      highlight: event.target,
      element: (viewerIsShown && fieldHelpExists && activeElement) ? activeElement : undefined
    })),
    tap(({highlight, element}) => highlight.element = element)
  );
  topLevelDiv.appendChild(highlight.element);

  const createLaser = ({logo, from}: { logo: HTMLElement, from: HTMLElement }) => {
    const element = document.createElement('teal-laser') as HTMLElement & { from: HTMLElement, to: HTMLElement };
    element.setAttribute('imagewidth', '120');
    element.setAttribute('imageheight', '120');
    element.setAttribute('imageoffsetwidth', '16');
    element.setAttribute('imageoffsetheight', '21');
    element.setAttribute('fromoffsety', '-12');
    element.setAttribute('tooffsetx', '-5');

    element.appendChild(logo);

    element.style.position = 'absolute';
    element.style.top = '0';
    element.style.left = '0';
    element.style.zIndex = '1';

    const readyObservable = fromEvent(element, 'teal-laser-ready').pipe(shareReplay(1));
    readyObservable.pipe(tap(() => element.from = from), take(1)).subscribe();
    return {
      element,
      readyObservable
    };
  };
  const laser = createLaser({
    logo: (() => {
      const img = document.createElement('img');
      img.src = 'data:image/png;base64,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'
      img.style.width = '56px';
      img.style.height = '56px';
      img.style.paddingBottom = '12px';

      const container = document.createElement('div');
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.display = 'flex';
      container.style.alignItems = 'flex-end';
      container.style.justifyContent = 'center';
      container.appendChild(img);

      return container;
    })(),
    from: component
  });

  const laserObservable = combineLatest([laser.readyObservable, showSubject, activeHelpPageSubject.pipe(map(help => help !== null && help.type === 'FIELD')), activeElementSubject]).pipe(
    map(([event, viewerIsShown, fieldHelpExists, activeElement]) => ({
      laser: event.target,
      to: (viewerIsShown && fieldHelpExists && activeElement) ? activeElement : undefined,
      show: viewerIsShown
    })),
    tap(({laser, to, show}) => {
      laser.show = show;
      laser.to = to;
    })
  );
  topLevelDiv.appendChild(laser.element);

  const cssLoaded = () => document.documentElement.style.setProperty('--teal-viewer-visibility', 'visible');

  merge(
    focusObservable,
    pageConfigObservable,
    showLocalStorageObservable,
    useInitialHelpPageObservable,
    useHelpPageWithActiveElementObservable,
    previousPageObservable,
    nextPageObservable,
    openButtonObservable,
    hoverButtonObservable,
    highlightObservable,
    laserObservable
  )
    .pipe(takeUntil(onDestroySubject)).subscribe();

  document.body.appendChild(topLevelDiv);
</script>
<style>
    teal-iframe-video {
        position: fixed;
        top: 20%;
        right: 350px;
    }

    :host{
        z-index: 2147483647;
        visibility: var(--teal-viewer-visibility, hidden);
        width: 300px;
    }


</style>
<svelte:options tag="teal-viewer"/>
<svelte:window
        on:teal-hover-button-click={(event) => activeElement = (event.detail.element)}
        on:teal-open-button-click={() => show = true}
/>

<svelte:head>
  <script type="text/javascript">
     window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
     heap.load("1306313961");
  </script>
  <script src="https://components.dobbi.app/teal-component/container-{tealComponentVersion}/dist/index.js">
  </script>
  <script src="https://components.dobbi.app/teal-component/logo-{tealComponentVersion}/dist/index.js">
  </script>
  <script src="https://components.dobbi.app/teal-component/hover-button-{tealComponentVersion}/dist/index.js">
  </script>
  <script src="https://components.dobbi.app/teal-component/open-button-{tealComponentVersion}/dist/index.js">
  </script>
  <script src="https://components.dobbi.app/teal-component/highlight-{tealComponentVersion}/dist/index.js">
  </script>
  <script src="https://components.dobbi.app/teal-component/laser-{tealComponentVersion}/dist/index.js">
  </script>
  <script src="https://components.dobbi.app/teal-component/iframe-video-{tealComponentVersion}/dist/index.js">
  </script>
  <script src="https://components.dobbi.app/teal-component/track-{tealComponentVersion}/dist/index.js">
  </script>
</svelte:head>

<link rel="stylesheet" type="text/css" href={css} on:load={cssLoaded}/>
<div class="viewer">
  <teal-track ns="{ns}" pageid="{pageid}" helpid="{helpid}">
  </teal-track>
  <teal-iframe-video
          src="{iframeVideoSrc}"
          on:teal-iframe-video-close={() => iframeVideoSrc = ""}
  >
  </teal-iframe-video>
  <teal-container
          ns="{ns}"
          pageid="{pageid}"
          helpid="{activeHelpPage ? activeHelpPage.helpId : undefined}"
          {...mayShowProperty()}
          on:teal-container-close="{() => show = false}"
          on:teal-help-content-action-previous="{previous}"
          on:teal-help-content-action-next="{next}"
          on:teal-help-content-action-browserBack="{() => window.history.back()}"
          on:teal-help-content-show-iframe="{(event) => {iframeVideoSrc = event.detail.src}}">
    <teal-logo slot="open">
    </teal-logo>
  </teal-container>
</div>
